import styled from "styled-components"
import {
  NoTableData,
  Table,
  TableContainer,
  Td,
  Th,
} from "~components/styled/table"
import { UserShape } from "~shapes/user"
import LoadingTable from "~components/LoadingTable"
import SkeletonWrapper from "~components/SkeletonWrapper"
import { useNavigate } from "react-router-dom"
import { isUserActive } from "~components/companies/utils/user"

import PATHS, { getFullPath } from "~constants/paths"
import UserRow from "~components/users/UserRow"

export interface UsersListProps {
  users: UserShape[]
  isLoading: boolean
  onClickUserEdit: (id: string) => void
  onClickUserDelete: (user: UserShape) => void
  onRefetchUsers: () => {}
  onPageChange: (id: number) => void
}

const columns = [
  { id: "status", label: "", width: "72px" },
  { id: "name", label: "Name", width: "auto" },
  { id: "email", label: "Email", width: "auto" },
  { id: "since", label: "Joined", width: "100px" },
  { id: "actions", label: "", width: "72px" },
]

const CompactTableWrapper = styled(TableContainer)`
  table {
    border-spacing: 0 3px;
  }

  table tbody tr {
    height: 42px;
  }
  table td {
    padding: 7px 10px;
  }
  table th {
    padding: 5px 10px 7px;
    font-size: 10px;
    letter-spacing: 0.35px;
  }
`

const UsersList = (props: UsersListProps) => {
  const { users, onClickUserEdit, isLoading, onClickUserDelete } = props
  const navigate = useNavigate()

  const isFirstLoad: boolean = !users.length && isLoading
  const noUsersFound: boolean = !users.length && !isLoading

  const getStatusLabel = (user: UserShape) =>
    isUserActive(user) ? "Active" : "Inactive"

  const userDetailPath = (email: string): string => {
    const encodedEmail = encodeURIComponent(email)
    return `${getFullPath(PATHS.users)}/${encodedEmail}`
  }

  const handleUserView = (email: string) => {
    navigate(userDetailPath(email), { replace: false })
  }

  return (
    <CompactTableWrapper style={{ maxHeight: "560px", overflowY: "auto" }}>
      <Table style={{ tableLayout: "auto" }}>
        <thead>
          <tr>
            {columns.map(col => (
              <Th
                $sticky
                key={col.id}
                style={{
                  width: col.width,
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: "uppercase" as const,
                  letterSpacing: "0.35px",
                }}
              >
                {col.label}
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isFirstLoad && (
            <LoadingTable rowHeight="42px" columns={5}>
              <Td>
                <SkeletonWrapper isLoading={true} width={52} height={22} />
              </Td>
            </LoadingTable>
          )}
          {users &&
            users.map(user => (
              <UserRow
                key={user.id}
                user={user}
                isLoading={isLoading}
                onClickUserEdit={onClickUserEdit}
                onClickUserDelete={onClickUserDelete}
                onViewUser={handleUserView}
                statusLabel={getStatusLabel(user)}
              />
            ))}
        </tbody>
      </Table>
      {noUsersFound && (
        <NoTableData style={{ fontSize: 13, color: "#7C8495" }}>
          No users found.
        </NoTableData>
      )}
    </CompactTableWrapper>
  )
}

export default UsersList
